import {ReactElement, useEffect, useRef, useState} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {ChevronRightIcon, PlusIconCorrect} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineAssistantConversation} from './DopamineAssistantConversation'
import {DopamineAssistantHistory} from './DopamineAssistantHistory'
import {DopamineAssistantMark} from './DopamineAssistantMark'
import {
  DopamineAssistantStorageScope,
  subscribeToDopamineAssistantCollapse,
} from './DopamineAssistant.utils'
import {useDopamineAssistantSession} from './useDopamineAssistantSession'
import './DopamineAssistant.css'

const PANEL_ID = 'dopamine-assistant-panel'

interface DopamineAssistantShellProps {
  className?: string
  isVisible: boolean
  storageScope: DopamineAssistantStorageScope | null
}

export const DopamineAssistantShell = ({
  className,
  isVisible,
  storageScope,
}: DopamineAssistantShellProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const openButtonRef = useRef<HTMLButtonElement>(null)
  const collapseButtonRef = useRef<HTMLButtonElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const shellRef = useRef<HTMLElement>(null)
  const hasToggledRef = useRef(false)
  const session = useDopamineAssistantSession({
    enabled: isExpanded && isVisible,
    storageScope,
  })

  useEffect(() => {
    if (!isVisible || !hasToggledRef.current) return

    let focusTarget: HTMLElement | null = openButtonRef.current
    if (isExpanded) {
      const composer = composerRef.current
      focusTarget =
        composer && !composer.disabled ? composer : collapseButtonRef.current
    }
    focusTarget?.focus()
  }, [isExpanded, isVisible])

  useEffect(() => {
    if (isVisible) return

    hasToggledRef.current = false
    setIsExpanded(false)
    setIsHistoryOpen(false)
  }, [isVisible])

  useEffect(
    () =>
      subscribeToDopamineAssistantCollapse(() => {
        hasToggledRef.current = true
        setIsHistoryOpen(false)
        setIsExpanded(false)
      }),
    [],
  )

  const updateExpandedState = (expanded: boolean): void => {
    hasToggledRef.current = true
    if (!expanded) setIsHistoryOpen(false)
    setIsExpanded(expanded)
  }

  const startNewChat = (): void => {
    setIsHistoryOpen(false)
    session.startNewChat()
    window.requestAnimationFrame(() => {
      const composer = composerRef.current
      const focusTarget =
        composer && !composer.disabled ? composer : collapseButtonRef.current
      focusTarget?.focus()
    })
  }

  useEffect(() => {
    if (!isExpanded) return

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (
        event.key !== KEY_CODES.ESCAPE ||
        !shellRef.current?.contains(document.activeElement)
      ) {
        return
      }

      event.preventDefault()
      if (isHistoryOpen) {
        setIsHistoryOpen(false)
        return
      }

      hasToggledRef.current = true
      setIsExpanded(false)
    }

    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => document.removeEventListener('keydown', handleDocumentKeyDown)
  }, [isExpanded, isHistoryOpen])

  return (
    <aside
      aria-label={
        isExpanded ? t('DOPAMINE_ASSISTANT') : t('DOPAMINE_ASSISTANT_RAIL')
      }
      className={classNames(
        'dopamine-assistant-root peer/dopamine karla relative isolate h-full shrink-0 overflow-hidden border-r-0.5 border-white/10 text-white',
        'transition-width ease-dope-out motion-reduce:transition-none',
        isExpanded ? 'w-dopamine-assistant' : 'w-12',
        className,
      )}
      data-cy={
        isExpanded ? 'dopamine-assistant-panel' : 'dopamine-assistant-rail'
      }
      data-expanded={isExpanded}
      hidden={!isVisible}
      id={PANEL_ID}
      ref={shellRef}
    >
      <div
        className={classNames(
          'relative flex h-full min-w-dopamine-assistant flex-col',
          isExpanded ? 'bg-transparent' : 'bg-black/50 backdrop-blur-xl',
        )}
      >
        <div
          className={classNames(
            'dopamine-expanded-surface min-h-0 flex-1 flex-col',
            isExpanded ? 'flex' : 'hidden',
          )}
          hidden={!isExpanded}
        >
          <span aria-hidden="true" className="dopamine-shell-aurora" />
          <span aria-hidden="true" className="dopamine-shell-grain" />
          <header className="dopamine-header">
            <div className="dopamine-header-brand">
              <span className="dopamine-mark-chip flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white shadow-inset-subtle">
                <DopamineAssistantMark className="h-4 w-4" />
              </span>
              <h2
                aria-label={t('DOPAMINE')}
                className="dopamine-wordmark min-w-0 truncate text-sm leading-none text-white/90"
              >
                <span className="abc-bold">DOP</span>
                <span className="cositimes">AMINE</span>
              </h2>
            </div>
            <div className="dopamine-header-actions">
              {session.isApiAvailable &&
              session.hasConversation &&
              !session.isHistoryError ? (
                <button
                  aria-label={t('DOPAMINE_NEW_CHAT')}
                  className="dopamine-new-chat-button"
                  title={t('DOPAMINE_NEW_CHAT')}
                  type="button"
                  onClick={startNewChat}
                >
                  <PlusIconCorrect
                    classes="dopamine-new-chat-icon h-3.5 w-3.5"
                    height={14}
                    strokeColor="currentColor"
                    width={14}
                  />
                  <span>{t('DOPAMINE_NEW')}</span>
                </button>
              ) : null}
              {session.isApiAvailable ? (
                <DopamineAssistantHistory
                  isOpen={isHistoryOpen}
                  session={session}
                  onClose={() => setIsHistoryOpen(false)}
                  onOpen={() => setIsHistoryOpen(true)}
                />
              ) : null}
              <button
                aria-controls={PANEL_ID}
                aria-expanded="true"
                aria-label={t('COLLAPSE_DOPAMINE_ASSISTANT')}
                className="dopamine-header-icon-button dopamine-collapse-button"
                data-tip={t('COLLAPSE_DOPAMINE_ASSISTANT')}
                data-cy="dopamine-assistant-collapse"
                ref={collapseButtonRef}
                type="button"
                onClick={() => updateExpandedState(false)}
              >
                <ChevronRightIcon
                  classes="rotate-180"
                  height={12}
                  strokeColor="currentColor"
                  strokeWidth={1.5}
                  width={7}
                />
              </button>
            </div>
          </header>
          {session.isApiAvailable ? (
            <DopamineAssistantConversation
              composerRef={composerRef}
              isVisible={isExpanded}
              onStartNewChat={startNewChat}
              session={session}
            />
          ) : (
            <output
              className="relative flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center"
              data-cy="dopamine-assistant-unavailable"
            >
              <DopamineAssistantMark className="mb-4 h-8 w-8" gradient />
              <span className="abc-bold text-sm text-white/90">
                {t('DOPAMINE_ASSISTANT_UNAVAILABLE')}
              </span>
              <span className="mt-2 text-xs leading-5 text-white/55">
                {t('DOPAMINE_ASSISTANT_UNAVAILABLE_DESCRIPTION')}
              </span>
            </output>
          )}
        </div>
        <button
          aria-controls={PANEL_ID}
          aria-expanded="false"
          aria-label={t('OPEN_DOPAMINE_ASSISTANT')}
          className={classNames(
            'dopamine-rail-surface relative h-full w-12 flex-col items-center pt-1.5',
            isExpanded ? 'hidden' : 'flex',
          )}
          data-cy="dopamine-assistant-open"
          hidden={isExpanded}
          ref={openButtonRef}
          title={t('OPEN_DOPAMINE_ASSISTANT')}
          type="button"
          onClick={() => updateExpandedState(true)}
        >
          <span
            aria-hidden="true"
            className="absolute right-0 top-0 h-full w-px bg-gradient-to-b from-white/20 via-white/10 to-transparent"
          />
          <span className="dopamine-rail-button">
            <DopamineAssistantMark className="relative h-4.5 w-4.5" gradient />
          </span>
        </button>
      </div>
    </aside>
  )
}
